<template>
  <div class="container" dir="rtl">
    <br>
    <div class="col-12 text-center g">
      <h4>
        <i class="fa fa-file-pdf-o text-danger"></i>
        اصلاح ملفات PDF
        <br>
        <small class="text-muted">ازالة العناصر النشطة من الملف ليقبله نظام مدرستي</small>
      </h4>
    </div>

    <div class="card" style="border: 2px solid #888; border-radius: 20px">
      <div class="card-body">

        <!-- اختيار الملف -->
        <div
          v-if="!result"
          class="dropzone text-center"
          :class="{ 'dropzone-over': dragging }"
          @click="$refs.fileInput.click()"
          @dragover.prevent="dragging = true"
          @dragleave.prevent="dragging = false"
          @drop.prevent="onDrop($event)"
        >
          <input
            ref="fileInput"
            type="file"
            accept="application/pdf,.pdf"
            style="display: none"
            @change="onPick($event)"
          >
          <h1 class="text-muted" style="margin-bottom: 0px">
            <i class="fa fa-cloud-upload"></i>
          </h1>
          <strong>اضغط هنا لاختيار ملف PDF</strong>
          <br>
          <small class="text-muted">أو قم بسحب الملف وإفلاته هنا</small>
        </div>

        <!-- الملف المختار -->
        <div v-if="file && !result" class="col-12 g">
          <div class="alert alert-secondary">
            <i class="fa fa-file"></i>
            <strong>{{ file.name }}</strong>
            <span class="text-muted">({{ humanSize(file.size) }})</span>
          </div>
          <div class="text-center">
            <button class="btn btn-primary btn-lg" :disabled="working" @click="fixFile()">
              <span v-if="!working">
                <i class="fa fa-magic"></i>
                اصلاح الملف الآن
              </span>
              <span v-if="working">
                جاري الاصلاح...
              </span>
            </button>
          </div>
        </div>

        <!-- خطأ -->
        <div v-if="error" class="alert alert-danger g">
          <i class="fa fa-times"></i>
          {{ error }}
        </div>

        <!-- النتيجة -->
        <div v-if="result">
          <div class="alert alert-success text-center">
            <h4 class="text-success">تم اصلاح الملف بنجاح</h4>
            <span v-if="result.totalRemoved">
              تمت ازالة {{ result.totalRemoved }} عنصر نشط من الملف.
            </span>
            <span v-if="!result.totalRemoved">
              الملف لم يكن يحتوي على عناصر نشطة، وتمت اعادة بنائه بشكل نظيف.
            </span>
          </div>

          <div v-if="result.encrypted" class="alert alert-warning">
            <i class="fa fa-exclamation-triangle"></i>
            هذا الملف كان محمي بكلمة مرور، قد لا يظهر محتواه بشكل صحيح بعد الاصلاح.
          </div>

          <div class="table-responsive" v-if="Object.keys(result.removed).length">
            <table class="table table-sm table-bordered table-hover">
              <thead>
                <tr>
                  <th>العنصر</th>
                  <th>النوع</th>
                  <th>العدد</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(count, key) in result.removed" :key="key">
                  <td><code>{{ key }}</code></td>
                  <td>{{ describe(key) }}</td>
                  <td>{{ count }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="alert" :class="result.stillFound.length ? 'alert-warning' : 'alert-light border'">
            <strong>فحص الملف بعد الاصلاح:</strong>
            <br>
            <span v-if="!result.stillFound.length" class="text-success">
              لم يتم العثور على اي عنصر نشط في الملف.
            </span>
            <span v-if="result.stillFound.length" class="text-danger">
              ما زال الفحص يجد: {{ result.stillFound.join('، ') }}
              <br>
              <small class="text-muted">
                قد تكون هذه نتائج غير حقيقية ناتجة عن ضغط محتوى الملف، جرب رفع الملف وسيعمل غالباً.
              </small>
            </span>
          </div>

          <div class="col-12 text-center g">
            <a :href="result.url" :download="result.name" class="btn btn-success btn-lg">
              <i class="fa fa-download"></i>
              تحميل الملف بعد الاصلاح
            </a>
            <br><br>
            <button class="btn btn-outline-secondary" @click="reset()">
              اصلاح ملف آخر
            </button>
          </div>
        </div>

      </div>
    </div>
    <br><br>
  </div>
</template>

<script>
import {
  PDFDocument,
  PDFName,
  PDFRef,
  PDFArray,
  PDFDict,
} from 'pdf-lib'

// مفاتيح تُحذف من كتالوج الملف
const CATALOG_KEYS = ['OpenAction', 'AA', 'Names', 'AcroForm', 'JavaScript', 'JS', 'Launch', 'EmbeddedFiles', 'URI']
// مفاتيح تُحذف من اي قاموس داخل الملف
const DICT_KEYS = ['AA', 'JS', 'JavaScript', 'Launch', 'URI', 'SubmitForm', 'ImportData', 'GoToR', 'RichMedia', 'Movie', 'Sound', 'Rendition', 'EmbeddedFiles', 'OpenAction']
// كائنات تُحذف بالكامل حسب النوع
const DEAD_TYPES = ['Annot', 'Action', 'Filespec', 'RichMediaInstance']
// كائنات تُحذف بالكامل حسب نوع الاجراء
const ACTION_SUBTYPES = ['JavaScript', 'URI', 'Launch', 'SubmitForm', 'ImportData', 'GoTo', 'GoToR', 'GoToE', 'Movie', 'Sound', 'Rendition', 'Named', 'Hide', 'SetOCGState', 'Thread']
// ما يبحث عنه الفاحص في بايتات الملف
const SCAN_MARKERS = ['/AA', '/OpenAction', '/JavaScript', '/JS', '/Launch', '/EmbeddedFile', '/AcroForm', '/RichMedia', '/Annots', '/Link', '/URI', '/Action', '/Movie', '/Sound', '/3D']

const DESCRIPTIONS = {
  '/OpenAction': 'اجراء يعمل تلقائياً عند فتح الملف',
  '/AA': 'اجراءات تلقائية اضافية',
  '/Names': 'شجرة اسماء قد تحتوي على اكواد',
  '/JavaScript': 'كود جافاسكربت',
  '/JS': 'كود جافاسكربت',
  '/Launch': 'تشغيل برامج خارجية',
  '/EmbeddedFiles': 'ملفات مرفقة داخل الملف',
  '/AcroForm': 'نماذج تفاعلية',
  '/Annots': 'روابط وتعليقات تفاعلية',
  '/Annot': 'روابط وتعليقات تفاعلية',
  '/Action': 'اجراء تفاعلي',
  '/URI': 'رابط خارجي',
  '/Link': 'رابط',
  '/Filespec': 'مرفق',
  '/SubmitForm': 'ارسال بيانات نموذج',
  '/ImportData': 'استيراد بيانات',
  '/GoToR': 'انتقال الى ملف خارجي',
  '/RichMedia': 'وسائط تفاعلية',
  '/Movie': 'مقطع فيديو',
  '/Sound': 'مقطع صوت',
  '/Rendition': 'وسائط تفاعلية',
}

export default {
  data() {
    return {
      file: null,
      dragging: false,
      working: false,
      error: '',
      result: null,
    }
  },
  beforeDestroy() {
    this.revoke()
  },
  methods: {
    onPick(e) {
      this.setFile(e.target.files && e.target.files[0])
    },
    onDrop(e) {
      this.dragging = false
      this.setFile(e.dataTransfer.files && e.dataTransfer.files[0])
    },
    setFile(f) {
      this.error = ''
      if (!f) {
        return
      }
      if (f.type !== 'application/pdf' && !/\.pdf$/i.test(f.name)) {
        this.error = 'الملف المختار ليس ملف PDF.'
        return
      }
      this.file = f
    },
    humanSize(bytes) {
      if (bytes < 1024) {
        return `${bytes} بايت`
      }
      if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(1)} كيلوبايت`
      }
      return `${(bytes / 1024 / 1024).toFixed(1)} ميجابايت`
    },
    describe(key) {
      return DESCRIPTIONS[key] || 'عنصر نشط'
    },
    // فحص مبدئي لبايتات الملف بحثاً عن العناصر النشطة
    scan(bytes) {
      let text = ''
      const chunk = 60000
      for (let i = 0; i < bytes.length; i += chunk) {
        text += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk))
      }
      return SCAN_MARKERS.filter(m => text.indexOf(m) !== -1)
    },
    async fixFile() {
      const g = this
      g.working = true
      g.error = ''
      try {
        const bytes = new Uint8Array(await g.file.arrayBuffer())
        const doc = await PDFDocument.load(bytes, { ignoreEncryption: true, updateMetadata: false })

        const removed = {}
        const hit = k => { removed[k] = (removed[k] || 0) + 1 }

        CATALOG_KEYS.forEach(k => {
          if (doc.catalog.get(PDFName.of(k)) !== undefined) {
            doc.catalog.delete(PDFName.of(k))
            hit(`/${k}`)
          }
        })

        doc.getPages().forEach(page => {
          const annots = page.node.lookupMaybe(PDFName.of('Annots'), PDFArray)
          if (annots) {
            annots.asArray().forEach(a => {
              if (a instanceof PDFRef) {
                doc.context.delete(a)
                hit('/Annots')
              }
            })
          }
          const raw = page.node.get(PDFName.of('Annots'))
          if (raw instanceof PDFRef) {
            doc.context.delete(raw)
          }
          page.node.delete(PDFName.of('Annots'))
          if (page.node.get(PDFName.of('AA')) !== undefined) {
            page.node.delete(PDFName.of('AA'))
            hit('/AA')
          }
        })

        doc.context.enumerateIndirectObjects().forEach(([ref, obj]) => {
          if (!(obj instanceof PDFDict)) {
            return
          }
          const type = obj.get(PDFName.of('Type'))
          if (type && DEAD_TYPES.some(t => type === PDFName.of(t))) {
            doc.context.delete(ref)
            hit(String(type))
            return
          }
          const sub = obj.get(PDFName.of('S'))
          if (sub && ACTION_SUBTYPES.some(t => sub === PDFName.of(t))) {
            doc.context.delete(ref)
            hit(String(sub))
            return
          }
          DICT_KEYS.forEach(k => {
            if (obj.get(PDFName.of(k)) !== undefined) {
              obj.delete(PDFName.of(k))
              hit(`/${k}`)
            }
          })
        })

        const out = await doc.save({ useObjectStreams: false })

        g.revoke()
        g.result = {
          removed,
          totalRemoved: Object.values(removed).reduce((a, b) => a + b, 0),
          stillFound: g.scan(out),
          encrypted: !!doc.isEncrypted,
          url: URL.createObjectURL(new Blob([out], { type: 'application/pdf' })),
          name: `${g.file.name.replace(/\.pdf$/i, '')}-fixed.pdf`,
        }
      } catch (e) {
        g.error = `تعذر قراءة الملف أو اصلاحه، تأكد أن الملف سليم. (${e.message})`
      }
      g.working = false
    },
    revoke() {
      if (this.result && this.result.url) {
        URL.revokeObjectURL(this.result.url)
      }
    },
    reset() {
      this.revoke()
      this.result = null
      this.file = null
      this.error = ''
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },
  },
}
</script>

<style scoped>
.dropzone {
  border: 2px dashed #bbb;
  border-radius: 15px;
  padding: 40px 15px;
  cursor: pointer;
  transition: 0.2s;
}
.dropzone:hover,
.dropzone-over {
  border-color: #7695FF;
  background: #f4f7ff;
}
</style>
